import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import {
  Dialog,
  InputAdornment,
  IconButton,
  Collapse,
  Fade,
  TextField,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import {
  DialogHeader,
  DialogHeaderTitle,
  DialogActionsOverride,
  DialogContentContainer,
  ActionContainer,
} from '../../../Shared/styles/dialog';
import SelectedAssetsInfo from '../../../Shared/Publish/PublishProfilesDialog/SelectedAssetsInfo/component';
import toast, { superAdminToast } from '../../../../utils/toast';
import { SharedAssetExpiration, VAULT_VIDEO_TYPE } from '../../../../constants';
import { VaultButton as Button, Warning } from '../../../Shared';
import ValidationError from '../../../Shared/ValidationError';
import {
  ControlContainer,
  Label,
  ValuesContainer,
  Value,
} from './styles';
import { IAppState } from '../../../../state/reducers';
import {
  ValidationKey,
  toggleEmailDialog,
  updateEmail,
  updateEmailNote,
  updateDaysToExpire,
  addEmail,
  removeEmail,
  clearAll,
} from '../../../../state/shareEmailDialog';
import { selectIsUserVaultSuperAdmin, selectUserRole } from '../../../../state/user/userOrgRole';
import { selectValidation } from '../../../../state/validation';
import {
  IState,
  IProps,
  IStateProps,
  IDispatchProps,
} from './interfaces';
import { sendEmail } from '../../../../api/vault/email';
import { getAssetAttachments } from '../../../../api/vault/assetAttachments';
import { IAttachment } from '../../../../api/vault';
import { getDownloadLink } from '../../../../api/vault/assetDetails';
import { createAssetAccessLog, MetadataKeys } from '../../../../api/vault/logs';

class ShareEmailDialog extends PureComponent<IProps> {
  readonly state: IState = {
    canShare: true,
    autoCompleteOverride: false,
  };

  onExit = () => {
    const {
      clearAllConnect,
      onClose,
    } = this.props;

    this.setState({
      autoCompleteOverride: false,
    });
    clearAllConnect();
    onClose();
  };

  onAddEmailAddress = (e?: React.KeyboardEvent) => {
    if (e) {
      if (e.key === 'Enter') {
        const ref = e.nativeEvent.srcElement;
        // @ts-ignore
        ref.blur();
      } else {
        return;
      }
    }
    const {
      email,
      emailAddresses,
      onAddEmail,
    } = this.props;
    // note: timeout hack for nextTick
    // allows us to add the email after update occurs on input change
    setTimeout(() => {
      if (!emailAddresses.includes(email.toLowerCase())) {
        onAddEmail();
      }
    }, 0);
  };

  onShareAssets = async () => {
    const {
      selectedAssets,
      emailNote,
      daysToExpire,
      emailAddresses,
      userRole,
      isVaultSuperAdmin,
    } = this.props;

    const { userVaultRoles } = userRole;
    const { orgName } = userVaultRoles[0];

    this.setState({
      canShare: false,
    });

    const assets = await Promise.all(
      selectedAssets.map(async (asset) => {
        const { _id } = asset;
        const { title, type, videoType } = asset._source;
        let link: string;
        let attachmentLink: IAttachment;
        try {
          const response = await getDownloadLink({ id: _id }, daysToExpire * 24, 'share');
          link = response.data.data;
          const parsedLink = decodeURIComponent(link).split(/filename=(.*\.)/);
          if (videoType === VAULT_VIDEO_TYPE.Exchange && parsedLink.length === 3) {
            const fileName = `${parsedLink[1]}xchange`;
            const attachmentsResponse = await getAssetAttachments(fileName, _id, daysToExpire * 24);
            if (attachmentsResponse.data.data.length) {
              [attachmentLink] = attachmentsResponse.data.data;
            }
          } else {
            console.warn(`[QA] could not parse filename from download link for asset "${_id}"`);
          }
        } catch (e) {
          console.error(`[QA] Could not find download link for asset "${_id}"`);
        }
        return {
          _id,
          title,
          type,
          link,
          attachmentLink: attachmentLink ? attachmentLink.url : null,
        };
      }) // eslint-disable-line
    );

    /* eslint-disable indent */
    const res = await sendEmail({
      toAddresses: emailAddresses,
      fromAddress: null,
      body: `
        The following asset(s) have been shared with you by ${orgName} (available for the next ${daysToExpire > 1 ? `${daysToExpire} days` : '24 hours'}),
        ${emailNote.length > 0 ? `<blockquote>${emailNote.replace(/\n/g, '<br />')}</blockquote>` : ''}
        ${assets.map(({
        title,
        type,
        link,
        attachmentLink,
      }) => `
          <br />
          <a href="${link}"><u>${title} (${type})</u></a>
          ${attachmentLink ? `| <a href="${attachmentLink}"><u>xchange file</u></a>` : ''}
        `)}
      `,
      subject: `Catapult Sports - ${orgName} has shared assets with you.`,
      isHtmlBody: true,
    });

    if (res.status === 200) {
      superAdminToast(isVaultSuperAdmin, 'Asset(s) successfully shared', { type: toast.TYPE.INFO });

      this.onExit();

      // VV-539: this row is kept, and is not the same event as the one the API now writes.
      // Requesting the link logs a `Shared` row server-side the moment the URL is issued,
      // which the caller can no longer suppress; this one is written only after the email
      // actually went out, and is the only record of who received it and when the link
      // expires. A share that fails to send therefore shows the issued link and no
      // recipients, which is the distinction worth keeping.
      //
      // `actionType` is unchanged, but POST /logs/assetAccess now validates it against
      // AccessLogActionTypes and ignores every other field in the body, so 'Shared' had to
      // be declared in that enum for this call to keep working.
      const metadata = emailAddresses.map((value) => ({
        label: MetadataKeys.Recipient,
        value,
      }));

      const expiration = new Date();
      expiration.setDate(expiration.getDate() + daysToExpire);
      metadata.push({
        label: MetadataKeys.Expiration,
        value: expiration.toISOString(),
      });

      const logRes = await Promise.all(selectedAssets.map(({
        _id: assetId,
        _source: {
          title: assetName,
        },
      }) => createAssetAccessLog({
        assetId,
        assetName,
        metadata,
        actionType: 'Shared',
      })));

      const failedRes = logRes.find(({ status }) => status !== 201);
      if (failedRes) {
        console.error('[QA] Failed creating shared asset access logs', failedRes);
      }
    } else {
      superAdminToast(isVaultSuperAdmin, 'An error occurred while sharing assets', { type: toast.TYPE.INFO });
    }

    this.setState({
      canShare: true,
    });
  };

  render() {
    const {
      selectedAssets,
      isOpen,
      validation,
      email,
      emailNote,
      daysToExpire,
      emailAddresses,
      cachedEmails,
      onUpdateEmail,
      onRemoveEmail,
      onUpdateEmailNote,
      onUpdateDaysToExpire,
    } = this.props;

    const { canShare, autoCompleteOverride } = this.state;

    let emailOptions = cachedEmails
      .filter((address) => !emailAddresses.includes(address));

    if (email) {
      emailOptions = emailOptions.filter((address) => address.startsWith(email));
    }

    return (
      <Dialog
        open={isOpen}
        fullWidth
        maxWidth="sm"
      >
        <DialogHeader
          data-auto="EmailShareDialog"
        >
          <DialogHeaderTitle>
            Share Asset(s)
          </DialogHeaderTitle>
        </DialogHeader>

        <Collapse in={validation.email.invalid}>
          <ValidationError errors={validation.email.errors} />
        </Collapse>

        <DialogContentContainer>
          <ControlContainer>
            <Label>
              E-Mail
            </Label>
            <Autocomplete
              freeSolo
              disableClearable
              clearOnEscape
              options={emailOptions}
              onInputChange={(_, value: string) => onUpdateEmail(value)}
              value={email}
              style={{ width: '100%' }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Enter email addresses for sharing assets"
                  onFocus={(e) => {
                    if (!autoCompleteOverride) {
                      e.target.setAttribute('autocomplete', 'new-password');
                      this.setState({
                        autoCompleteOverride: true,
                      });
                    }
                  }}
                  onKeyDown={(e) => this.onAddEmailAddress(e)}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onMouseDown={(e) => {
                            // note: using nextTick hack to bypass re-render on state changes
                            // @ts-ignore
                            setTimeout(() => document.activeElement.blur(), 150);
                            this.onAddEmailAddress();
                          }}
                          edge="end"
                          disabled={!email}
                          size="large"
                        >
                          <AddIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </ControlContainer>

          <ValuesContainer>
            <Warning
              isWarning={!emailAddresses.length}
              message="No emails configured for sharing"
            />
            <Warning
              isWarning={emailAddresses.includes(email.toLowerCase())}
              message={`Email "${email}" already exists`}
            />
            {emailAddresses.map((address, index) => (
              <Fade in key={index}>
                <Value>
                  {address}
                  <IconButton onClick={() => onRemoveEmail(index)} size="large">
                    <RemoveIcon />
                  </IconButton>
                </Value>
              </Fade>
            ))}
          </ValuesContainer>

          <ControlContainer>
            <Label>
              Note
            </Label>
            <TextField
              multiline
              fullWidth
              maxRows={2}
              aria-label="Optional Email Note"
              placeholder="(Optional) Enter note to include with email"
              value={emailNote}
              onChange={(e) => onUpdateEmailNote(e.target.value)}
            />
          </ControlContainer>

          <ControlContainer>
            <Label>
              Expire (Days)
            </Label>
            <TextField
              fullWidth
              type="number"
              inputProps={{
                min: SharedAssetExpiration.Min,
                max: SharedAssetExpiration.Max,
                step: '1',
              }}
              placeholder="Days Until Links Expires"
              value={daysToExpire}
              onChange={(e) => onUpdateDaysToExpire(e.target.value ? parseInt(e.target.value, 10) : 1)}
            />
          </ControlContainer>

          <ControlContainer>
            <SelectedAssetsInfo
              selectedAssets={selectedAssets}
              action="Sharing"
            />
          </ControlContainer>
        </DialogContentContainer>

        <DialogActionsOverride>
          <ActionContainer>
            <Button
              variant="plainOnPaper"
              onClick={() => this.onExit()}
              disabled={!canShare}
              data-auto="EmailCancelBtn"
            >
              Cancel
            </Button>
            <Button
              variant="primaryOnWhite"
              onClick={() => this.onShareAssets()}
              disabled={!emailAddresses.length || !canShare}
              data-auto="EmailShareBtn"
            >
              Share
            </Button>
          </ActionContainer>
        </DialogActionsOverride>
      </Dialog>
    );
  }
}

const mapStateToProps = (state: IAppState): IStateProps => {
  const validation = {
    email: selectValidation(ValidationKey.Email, state.validation),
  };

  return {
    isOpen: state.shareEmailDialog.isOpen,
    selectedAssets: state.shareEmailDialog.selectedAssets,
    email: state.shareEmailDialog.email,
    emailNote: state.shareEmailDialog.emailNote,
    daysToExpire: state.shareEmailDialog.daysToExpire,
    emailAddresses: state.shareEmailDialog.emailAddresses,
    cachedEmails: state.shareEmailDialog.cachedEmails,
    userRole: selectUserRole(state),
    validation,
    isVaultSuperAdmin: selectIsUserVaultSuperAdmin(state),
  };
};

const mapDispatchToProps: IDispatchProps = {
  onClose: toggleEmailDialog,
  onUpdateEmail: updateEmail,
  onUpdateEmailNote: updateEmailNote,
  onUpdateDaysToExpire: updateDaysToExpire,
  onAddEmail: addEmail,
  onRemoveEmail: removeEmail,
  clearAllConnect: clearAll,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ShareEmailDialog);
